import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Lock, Check, Star, Sparkles, Target, Share2 } from 'lucide-react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AchievementPosterGenerator } from '@/components/AchievementPosterGenerator';

interface Achievement {
  id: string;
  achievement_key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
  reward_points: number;
  is_hidden: boolean;
}

interface UserAchievement {
  achievement_id: string;
  progress: number;
  is_unlocked: boolean;
  unlocked_at: string;
  achievements: Achievement;
}

const AchievementWall: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<Record<string, UserAchievement>>({});
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<any>(null);

  const fetchAchievements = async () => {
    const { data } = await supabase.from('achievements').select('*').order('sort_order');
    setAchievements(data || []);
  };

  const fetchUserAchievements = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_achievements')
      .select('*, achievements(*)')
      .eq('user_id', user.id);
    
    const achievementMap: Record<string, UserAchievement> = {};
    data?.forEach((ua: any) => {
      achievementMap[ua.achievement_id] = ua;
    });
    setUserAchievements(achievementMap);
  };

  const handleCheckProgress = async () => {
    if (!user) return;
    
    setChecking(true);
    try {
      const { data, error } = await supabase.rpc('check_achievement_progress', { user_id_param: user.id });
      
      if (error) throw error;
      
      const result = data as { success: boolean; newly_unlocked: string[] };
      
      if (result.newly_unlocked && result.newly_unlocked.length > 0) {
        toast.success(`恭喜解锁新成就：${result.newly_unlocked.join('、')} 🎉`);
      } else {
        toast.info('暂无新成就解锁，继续加油！');
      }
      
      await Promise.all([fetchUserAchievements(), refreshProfile()]);
    } catch (err: any) {
      toast.error(err.message || '检查失败');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchAchievements(), fetchUserAchievements()]);
      setLoading(false);
    };
    init();
  }, [user]);

  const getUserAchievement = (achievementId: string) => {
    return userAchievements[achievementId] || { progress: 0, is_unlocked: false };
  };

  const getProgressPercentage = (achievement: Achievement) => {
    const ua = getUserAchievement(achievement.id);
    return Math.min((ua.progress / achievement.requirement_value) * 100, 100);
  };

  const unlockedCount = Object.values(userAchievements).filter(ua => ua.is_unlocked).length;
  const totalCount = achievements.filter(a => !a.is_hidden).length;

  const achievementsByCategory = (category: string) => {
    return achievements.filter(a => a.category === category && (!a.is_hidden || getUserAchievement(a.id).is_unlocked));
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'social': return '社交互动';
      case 'content': return '内容创作';
      case 'learning': return '学习成长';
      case 'special': return '特殊成就';
      default: return category;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Trophy className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-muted-foreground">加载中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        {/* 头部 */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-4">
            <Trophy className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            成就墙
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            完成挑战解锁成就，获得专属勋章和积分奖励
          </p>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-amber-500/5 to-amber-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" /> 已解锁成就
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{unlockedCount} / {totalCount}</div>
              <Progress value={(unlockedCount / totalCount) * 100} className="h-2 mt-3" />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-blue-500/5 to-blue-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Star className="h-4 w-4 text-blue-500" /> 成就积分
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {Object.values(userAchievements)
                  .filter(ua => ua.is_unlocked)
                  .reduce((sum, ua) => sum + (achievements.find(a => a.id === ua.achievement_id)?.reward_points || 0), 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">通过成就获得的总积分</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-purple-500/5 to-purple-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4 text-purple-500" /> 完成度
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">距离全成就还差 {totalCount - unlockedCount} 个</p>
            </CardContent>
          </Card>
        </div>

        {/* 检查进度按钮 */}
        <div className="flex justify-center mb-8">
          <Button
            size="lg"
            className="rounded-xl font-bold h-12 px-8 shadow-lg"
            onClick={handleCheckProgress}
            disabled={checking}
          >
            {checking ? (
              '检查中...'
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                检查成就进度
              </>
            )}
          </Button>
        </div>

        {/* 成就列表 */}
        <Tabs defaultValue="all" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-muted/50 p-1">
              <TabsTrigger value="all">全部</TabsTrigger>
              <TabsTrigger value="social">社交互动</TabsTrigger>
              <TabsTrigger value="content">内容创作</TabsTrigger>
              <TabsTrigger value="learning">学习成长</TabsTrigger>
              <TabsTrigger value="special">特殊成就</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {achievements.filter(a => !a.is_hidden || getUserAchievement(a.id).is_unlocked).map((achievement) => {
                const ua = getUserAchievement(achievement.id);
                const progress = getProgressPercentage(achievement);

                return (
                  <Card
                    key={achievement.id}
                    className={cn(
                      "rounded-2xl border-border/50 transition-all duration-300 relative overflow-hidden",
                      ua.is_unlocked ? "bg-gradient-to-br from-amber-500/5 to-amber-500/10 border-amber-500/20" : "hover:shadow-lg"
                    )}
                  >
                    {ua.is_unlocked && (
                      <div className="absolute top-3 right-3 z-10 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 bg-white/90 backdrop-blur-sm"
                          onClick={() => {
                            setSelectedAchievement({
                              ...achievement,
                              unlocked_at: ua.unlocked_at,
                            });
                            setPosterOpen(true);
                          }}
                        >
                          <Share2 className="h-3 w-3" />
                        </Button>
                        <Badge className="bg-amber-500 text-white border-none shadow-md">
                          <Check className="h-3 w-3 mr-1" /> 已解锁
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-center mb-4">
                        <div className={cn(
                          "text-6xl transition-all",
                          !ua.is_unlocked && "opacity-30 grayscale"
                        )}>
                          {achievement.icon}
                        </div>
                      </div>
                      <CardTitle className="text-center text-lg font-bold">{achievement.name}</CardTitle>
                      <CardDescription className="text-center text-sm">{achievement.description}</CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-center gap-2">
                        <Badge variant="outline">{getCategoryLabel(achievement.category)}</Badge>
                        {achievement.reward_points > 0 && (
                          <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                            +{achievement.reward_points} 积分
                          </Badge>
                        )}
                      </div>

                      {!ua.is_unlocked && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>进度</span>
                            <span className="font-bold">{ua.progress} / {achievement.requirement_value}</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      )}

                      {ua.is_unlocked && ua.unlocked_at && (
                        <div className="text-center text-xs text-muted-foreground">
                          解锁于 {new Date(ua.unlocked_at).toLocaleDateString('zh-CN')}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {['social', 'content', 'learning', 'special'].map((category) => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {achievementsByCategory(category).map((achievement) => {
                  const ua = getUserAchievement(achievement.id);
                  const progress = getProgressPercentage(achievement);

                  return (
                    <Card
                      key={achievement.id}
                      className={cn(
                        "rounded-2xl border-border/50 transition-all duration-300 relative overflow-hidden",
                        ua.is_unlocked ? "bg-gradient-to-br from-amber-500/5 to-amber-500/10 border-amber-500/20" : "hover:shadow-lg"
                      )}
                    >
                      {ua.is_unlocked && (
                        <div className="absolute top-3 right-3 z-10">
                          <Badge className="bg-amber-500 text-white border-none shadow-md">
                            <Check className="h-3 w-3 mr-1" /> 已解锁
                          </Badge>
                        </div>
                      )}

                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-center mb-4">
                          <div className={cn(
                            "text-6xl transition-all",
                            !ua.is_unlocked && "opacity-30 grayscale"
                          )}>
                            {achievement.icon}
                          </div>
                        </div>
                        <CardTitle className="text-center text-lg font-bold">{achievement.name}</CardTitle>
                        <CardDescription className="text-center text-sm">{achievement.description}</CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-center gap-2">
                          {achievement.reward_points > 0 && (
                            <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                              +{achievement.reward_points} 积分
                            </Badge>
                          )}
                        </div>

                        {!ua.is_unlocked && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>进度</span>
                              <span className="font-bold">{ua.progress} / {achievement.requirement_value}</span>
                            </div>
                            <Progress value={progress} className="h-2" />
                          </div>
                        )}

                        {ua.is_unlocked && ua.unlocked_at && (
                          <div className="text-center text-xs text-muted-foreground">
                            解锁于 {new Date(ua.unlocked_at).toLocaleDateString('zh-CN')}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* 海报生成器 */}
      {selectedAchievement && (
        <AchievementPosterGenerator
          achievement={selectedAchievement}
          username={profile?.username || 'User'}
          open={posterOpen}
          onOpenChange={setPosterOpen}
        />
      )}
    </Layout>
  );
};

export default AchievementWall;
